import { Box } from "@mui/material";
import { tracker } from "@openreplay/tracker";
import { useCallback, useContext } from "react";
import type { MouseEvent } from "react";

import { DATA_EXPLORER_ROUTE, getDataDetailUrl } from "@helpers/constants";
import { DASHBOARD_EVENTS } from "@helpers/events";
import { formatDateTimeString, formatDistance } from "@helpers/utils";

import renderer from "../../../data/defaultFieldRenderer";
import { useRowNavigation } from "../../../hooks/useRowNavigation";
import { GetStartedContext } from "../../app/components/GetStarted/GetStartedContext";
import { DataTable, type RequestState } from "./DataTable";

interface DataResourcesTableProps {
  dataResources: any[];
  requestState: RequestState;
  error: string | null;
}

const getDataDetailPath = (data: { id: string }) => getDataDetailUrl(data.id);

export const DataResourcesTable = ({
  dataResources,
  requestState,
  error,
}: DataResourcesTableProps) => {
  const { toggleDrawer } = useContext(GetStartedContext);
  const baseHandleRowClick = useRowNavigation<{ id: string }>(
    getDataDetailPath,
  );
  const handleRowClick = useCallback(
    (data: any, event?: MouseEvent<Element>) => {
      tracker.event(DASHBOARD_EVENTS.DATA_ROW_CLICKED);
      baseHandleRowClick(data, event);
    },
    [baseHandleRowClick],
  );

  const columns = [
    {
      header: "Name",
      accessor: (data: any) => renderer(data.name ?? data.id).node,
    },
    {
      header: "Created by",
      accessor: (data: any) => renderer(data.creator).node,
      padding: "none" as const,
    },
    {
      header: "Created",
      accessor: (data: any) =>
        renderer(
          formatDistance(data.created),
          formatDateTimeString(data.created),
        ).node,
    },
  ];

  const emptyStateMessage = (
    <>
      No data objects found. Try a different filter or go to{" "}
      <Box
        component="a"
        href={DATA_EXPLORER_ROUTE}
        sx={{ color: "primary.main" }}
      >
        Data Explorer
      </Box>{" "}
      to refine your search.
      <br />
      New to AI Toolkit?{" "}
      <Box
        component="a"
        href="#"
        onClick={() => toggleDrawer()}
        sx={{ color: "primary.main" }}
      >
        Get started
      </Box>
      .
    </>
  );

  return (
    <DataTable
      items={dataResources}
      columns={columns}
      requestState={requestState}
      error={error}
      emptyStateMessage={emptyStateMessage}
      seeAllLink={DATA_EXPLORER_ROUTE}
      onRowClick={handleRowClick}
    />
  );
};
